import cc from 'classcat'
import React, { AnchorHTMLAttributes, forwardRef, MouseEvent, ReactElement, Ref, useCallback, useMemo } from 'react'

import { Asable } from '../../types/asable'
import { getLinkProps } from '../../utils/getLinkProps'
import * as Styled from './styles'
import { ColorVariant } from './types/colorVariant'

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement>, Asable {
  colorVariant?: ColorVariant
  disabled?: boolean
  underlined?: boolean
  withAnimation?: boolean
  to?: string
}

export const Link = forwardRef((props: LinkProps, ref: Ref<HTMLAnchorElement>): ReactElement => {
  const {
    children,
    colorVariant = 'inherit',
    disabled,
    onClick,
    underlined = true,
    withAnimation,
    as,
    href,
    to,
    target,
    ...rest
  } = props

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault()
        return
      }

      onClick?.(event)
    },
    [disabled, onClick],
  )

  const linkProps: LinkProps | undefined = useMemo(
    () =>
      getLinkProps({
        as,
        href,
        to,
        target,
      }),
    [href, to, as, target],
  )

  return (
    <Styled.Link
      colorVariant={colorVariant}
      ref={ref}
      $disabled={disabled}
      onClick={handleClick}
      underlined={underlined}
      withAnimation={withAnimation}
      {...rest}
      {...linkProps}
      className={cc(['ds-link', { withAnimation, underlined }, rest.className])}
      data-testid="link"
    >
      {children}
    </Styled.Link>
  )
})
